import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';

import { fetchUserPreferences, resetPassword, updateUserPreferences } from 'api/UserPreferencesAPI';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { updateClipboard } from 'utils/clipboard';

import { fetchUserPreferencesResponse, userPreferencesErrorsResponse } from './actions';
import * as t from './actionTypes';
import { USER_PREFERENCES_ERRORS } from './constants';
import { Action, CopyToClipboardPayload, ProfileData } from './types';

const message = 'An error occurred. Please refresh the page or contact support.';
const failedSaveMsg = 'Unable to save changes. Please refresh the page or contact support.';

type ProfileAction = {
  type: string;
  callback: () => void;
  payload: ProfileData;
};

export function* createUserPreferencesErrorsSaga({ payload }: Action) {
  try {
    yield put(userPreferencesErrorsResponse({ errors: payload }));
  } catch (e) {
    // no-op
  }
}

export function* fetchUserPreferencesSaga() {
  try {
    const { data } = yield call(fetchUserPreferences);
    yield put(fetchUserPreferencesResponse({ payload: data }));
  } catch (err) {
    yield put(userPreferencesErrorsResponse({ errors: [USER_PREFERENCES_ERRORS.UNKNOWN_ERROR] }));
    yield put(addNegativeToast(message));
  }
}

export function* updateUserProfileSaga({ callback, payload }: ProfileAction) {
  try {
    /* Begin Validation */
    const errors = [];
    if (!payload.firstName) {
      errors.push(USER_PREFERENCES_ERRORS.MISSING_FIRST_NAME);
    }

    if (!payload.lastName) {
      errors.push(USER_PREFERENCES_ERRORS.MISSING_LAST_NAME);
    }

    if (errors.length) {
      yield put(userPreferencesErrorsResponse({ errors }));
      return;
    }
    /* End Validation */

    const { data } = yield call(updateUserPreferences, payload);
    yield put(fetchUserPreferencesResponse({ payload: data }));
    if (callback) {
      callback(); // call the callback if it exists
    }
    yield put(addPositiveToast('Successfully updated profile.'));
  } catch (err) {
    yield put(
      userPreferencesErrorsResponse({ errors: [USER_PREFERENCES_ERRORS.UPDATE_PROFILE_ERROR] }),
    );
    yield put(addNegativeToast(failedSaveMsg));
  }
}

export function* resetPasswordSaga(payload: any) {
  const { email } = payload;
  try {
    yield call(resetPassword, { email });
    const success = `Email containing a password reset link successfully sent to ${email}`;
    yield put(addPositiveToast(success));
  } catch (err) {
    const errorMsg = `Email containing a password reset link could not be sent to ${email}. Please try again.`;
    yield put(addNegativeToast(errorMsg));
  }
}

export function* copyToClipboardSaga(payload: { type: string } & CopyToClipboardPayload) {
  const { scratchCodes } = payload;
  try {
    yield updateClipboard(scratchCodes);
    yield put(addPositiveToast('Scratch codes copied to clipboard.'));
  } catch (e) {
    yield put(addNegativeToast('Could not copy scratch codes to clipboard.'));
  }
}

addToRootSaga([
  takeLatest([t.USER_PREFERENCES_ERRORS_REQUEST], createUserPreferencesErrorsSaga),
  takeLatest([t.FETCH_USER_PREFERENCES_REQUEST], fetchUserPreferencesSaga),
  takeLatest([t.UPDATE_USER_PROFILE_REQUEST], updateUserProfileSaga),
  takeLatest([t.RESET_PASSWORD_REQUEST], resetPasswordSaga),
  takeLatest([t.COPY_TO_CLIPBOARD], copyToClipboardSaga),
]);
